import {
  resetSearch,
  setCurrentFeatureID,
  setCurrentFloorID,
  unsetCurrentPoiID,
} from "@/features/cartography/cartographySlice";
import {
  selectConfigItem,
  setInitialConfigItem,
} from "@/features/config/configSlice";
import {
  clearDirectionsRoutes,
  setDirectionsSearchModeEnabled,
} from "@/features/directions/directionsSlice";
import { setSelectedFeatureId } from "@/features/editor/editorSlice";
import { selectFindMyCar } from "@/features/find_my_car/findMyCarSlice";
import {
  hydrateUserPreferencesColorTheme,
  UserPreferenceKeys,
} from "@/features/userPreferences/userPreferencesSlice";
import {
  AppUI,
  EditUI,
  selectEditMode,
  selectUIMode,
  setShowGeofencePopups,
  setUIMode,
  setUserSettingsPanelOpen,
} from "../app/appSlice";
import { selectCurrentLocationStatus } from "../location/locationSlice";
import {
  clearNavigation,
  setNavigationFrom,
  setNavigationTo,
  setSpeakAloudIndications,
} from "../navigation/navigationSlice";
import { regenerateSessionId } from "../session/sessionSlice";
import { closeShareModal } from "../share_map/shareMapSlice";
import {
  setDebouncedMapViewState,
  setExternalFeatures,
  setFollowUser,
  setMapViewState,
} from "./mapSlice";

import store from "@/app/store";
import { ColorThemes, type ReducedMotionTypes } from "@/features/app/constants";
import { handleSetCurrentPoiID } from "@/features/cartography/actions";
import { FontSizeEnum, type FontSizeItem } from "@/features/config/types";
import {
  selectDirectionsSearchModeEnabled,
  selectDirectionsSelectorNameSelected,
  selectRouteComputationStatus,
} from "@/features/directions/directionsSlice.selectors";
import {
  FormSelectorMode,
  RouteComputationStatus,
  RouteType,
} from "@/features/directions/types";
import { createPoiOverlayFeatureId } from "@/features/editor/utils/idGenerator";
import { FindMyCarStatusTypes } from "@/features/find_my_car/types";
import type { SliceProps as MapSliceProps } from "@/features/map/types";
import { getInitialValue } from "@/features/userPreferences/initialValues";
import { clearStoredUserPreferences } from "@/features/userPreferences/storage";
import { emmitEventSessionEnded } from "@/features/utils/eventEmmiter";
import { clamp, validateLatitude, validateLongitude } from "@/libs/geo";
import type IMDFFeature from "@/libs/imdf/feature";
import {
  type Camera,
  CURRENT_USER_LOCATION_ID,
  CUSTOM_POI_IDS,
  type ExternalFeature,
  type Poi,
} from "@/libs/models";
import { selectCurrentBuildingPois } from "../cartography/cartographySlice.selectors";
import {
  selectNavigationFrom,
  selectNavigationTo,
} from "../navigation/navigationSlice.selectors";
import { LocationStatusTypes } from "../utils/types";

export const handleFollowUser = async (payload: any) => {
  let followUser: boolean | undefined;
  // cheking types because of backward compatibility, previous version received raw boolean as payload
  if (typeof payload === "object" && "value" in payload) {
    followUser = (payload as { value: boolean }).value;
  } else if (typeof payload === "boolean") {
    followUser = payload;
  }
  if (followUser === undefined) return;
  const buildingID = store.getState()?.cartography?.currentBuildingID;
  const floorId = store.getState()?.cartography?.currentFloorID;
  floorId &&
    floorId > 0 &&
    buildingID &&
    buildingID > 0 &&
    store.dispatch(setFollowUser(followUser));
};

export const handleSetCamera = async (payload: Camera) => {
  const mapViewState = store.getState().map.mapViewState;

  const maxZoom = store.getState()?.map.mapViewLimits.maxZoom;
  const minZoom = store.getState()?.map.mapViewLimits.minZoom;
  const zoom = clamp(payload.zoom || mapViewState.zoom, minZoom, maxZoom);

  const bearing = payload.bearing ?? mapViewState.bearing;
  const pitch = payload.pitch ?? mapViewState.pitch;

  const transitionDuration =
    payload.transitionDuration != null
      ? Math.abs(payload.transitionDuration)
      : mapViewState.transitionDuration;

  const latitude =
    validateLatitude(payload.center?.lat) ?? mapViewState.latitude;
  const longitude =
    validateLongitude(payload.center?.lng) ?? mapViewState.longitude;

  store.dispatch(
    setDebouncedMapViewState({
      ...mapViewState,
      bearing: bearing,
      latitude: latitude,
      longitude: longitude,
      pitch: pitch,
      transitionDuration: transitionDuration,
      zoom: zoom,
    }),
  );
};

export const updateMapViewState = (
  data: Partial<MapSliceProps["mapViewState"]>,
) => {
  const previousState = store.getState().map.mapViewState;
  store.dispatch(setMapViewState({ ...previousState, ...data }));
};

interface ResetViewStateOptions {
  disableFollowUser?: boolean;
}

export const resetViewState = (options: ResetViewStateOptions = {}) => {
  const buildingId = store.getState().cartography.currentBuildingID;

  if (!buildingId) return;
  const currentBuilding =
    store.getState().cartography.buildings[buildingId].value;

  const mapViewState = store.getState().map.mapViewState;
  const mapViewLimits = store.getState().map.mapViewLimits;
  const initialFloor = store.getState().config.map.initialView.floor_id;

  const newState: any = {
    ...mapViewState,
    bearing: mapViewLimits.initialBearing,
    pitch: mapViewLimits.initialPitch,
    transitionDuration: 1000,
    zoom: mapViewLimits.initialZoom,
  };

  // reset view to initial values
  const currentFloorID = store.getState().cartography.currentFloorID;
  initialFloor !== currentFloorID &&
    currentBuilding.floors?.find((f) => f.id === initialFloor) &&
    store.dispatch(setCurrentFloorID(initialFloor));

  newState.latitude =
    mapViewLimits.initialLatitude || currentBuilding.getLocation().lat;
  newState.longitude =
    mapViewLimits.initialLongitude || currentBuilding.getLocation().lng;

  store.dispatch(setMapViewState(newState));
  store.dispatch(closeShareModal());
  store.dispatch(setUserSettingsPanelOpen(false));

  if (options.disableFollowUser !== false) {
    const { followUser } = store.getState().map;
    const currentLocation = store.getState().location.currentLocation;
    followUser && currentLocation && store.dispatch(setFollowUser(false));
  }
};

export const resetSession = () => {
  const buildingId = store.getState().cartography.currentBuildingID;
  const profileName = store.getState().session.profileName;

  resetViewState();
  cleanFilters();
  resetUserPreferences();
  buildingId && emmitEventSessionEnded(buildingId, profileName);
  store.dispatch(regenerateSessionId());
};

export const cleanFilters = () => {
  store.dispatch(resetSearch());
  store.dispatch(unsetCurrentPoiID());
  store.dispatch(clearNavigation());
  store.dispatch(clearDirectionsRoutes());
  store.dispatch(setUIMode(AppUI.explore));
};

const resetUserPreferences = () => {
  clearStoredUserPreferences();

  const language =
    store.getState().config.i18n.language ??
    store.getState().config.i18n.selectableLanguages[0];
  language && store.dispatch(setInitialConfigItem("i18n.language", language));

  const defaultRouteType = getInitialValue<RouteType>(
    UserPreferenceKeys.DEFAULT_ROUTE_TYPE,
    RouteType.CHOOSE_SHORTEST,
  );
  store.dispatch(
    setInitialConfigItem("directions.defaultRouteType", defaultRouteType),
  );

  const excludedTags = getInitialValue<string[]>(
    UserPreferenceKeys.EXCLUDED_TAGS,
    [],
  );
  store.dispatch(setInitialConfigItem("directions.excludedTags", excludedTags));

  const colorTheme = getInitialValue<ColorThemes>(
    UserPreferenceKeys.LAST_NON_ACCESSIBLE_COLOR_THEME,
    ColorThemes.system,
  );
  store.dispatch(setInitialConfigItem("styles.theme.colorTheme", colorTheme));
  store.dispatch(hydrateUserPreferencesColorTheme(colorTheme));

  const reducedMotion = getInitialValue<ReducedMotionTypes>(
    UserPreferenceKeys.REDUCED_MOTION,
    "user",
  );
  store.dispatch(setInitialConfigItem("ui.reducedMotion", reducedMotion));

  const fontSize = getInitialValue<FontSizeItem>(
    UserPreferenceKeys.FONT_SIZE,
    FontSizeEnum.MD as FontSizeItem,
  );
  store.dispatch(setInitialConfigItem("ui.fontSize", fontSize));

  const defaultSpeakIndications = getInitialValue<boolean>(
    UserPreferenceKeys.SPEAK_ALOUD_INDICATIONS,
    Boolean(store.getState().config.feature_flags.speak_indications),
  );
  store.dispatch(setSpeakAloudIndications(defaultSpeakIndications));
  store.dispatch(setShowGeofencePopups(true));
};

export const zoomIn = () => {
  const state = store.getState();
  const mapViewState = state?.map.mapViewState;
  const maxZoom = state?.map.mapViewLimits.maxZoom;

  const increasedZoom = Math.min(mapViewState.zoom + 1, maxZoom);

  if (increasedZoom !== mapViewState.zoom) {
    store.dispatch(
      setDebouncedMapViewState({
        ...mapViewState,
        zoom: increasedZoom,
      }),
    );
  }
};

export const zoomOut = () => {
  const state = store.getState();
  const mapViewState = state?.map.mapViewState;
  const minZoom = state?.map.mapViewLimits.minZoom;

  const decreasedZoom = Math.max(mapViewState.zoom - 1, minZoom);

  if (decreasedZoom !== mapViewState.zoom) {
    store.dispatch(
      setDebouncedMapViewState({
        ...mapViewState,
        zoom: decreasedZoom,
      }),
    );
  }
};

export const onPoiClick = (
  poiId?: Poi["id"],
  featureId?: IMDFFeature["id"],
) => {
  const currentBuildingPois = selectCurrentBuildingPois(store.getState());
  const currentEditMode = selectEditMode(store.getState());
  const routeComputationStatus = selectRouteComputationStatus(store.getState());
  const findMyCar = selectFindMyCar(store.getState());
  const uiMode = selectUIMode(store.getState());
  const directionsSearchModeEnabled = selectDirectionsSearchModeEnabled(
    store.getState(),
  );
  const directionsSelectorNameSelected = selectDirectionsSelectorNameSelected(
    store.getState(),
  );
  const fixedPoiID = selectConfigItem("map.initialView.fixedPoi")(
    store.getState(),
  );
  const navigationFrom = selectNavigationFrom(store.getState());
  const navigationTo = selectNavigationTo(store.getState());
  const currentLocationStatus = selectCurrentLocationStatus(store.getState());
  const allowReselectingTargetOnRoute = selectConfigItem(
    "feature_flags.allow_reselecting_route_target",
  )(store.getState());

  // Reset selected feature id
  store.dispatch(setCurrentFeatureID(null));

  // When using any editor mode that is not intended for regular exploration,
  // avoid triggering the standard POI selection logic.
  if (
    currentEditMode === EditUI.overlayLayers ||
    uiMode === AppUI.locationPicker
  ) {
    store.dispatch(setSelectedFeatureId(createPoiOverlayFeatureId(poiId)));
    featureId && store.dispatch(setCurrentFeatureID(featureId));
    return;
  }

  // Do not allow poi selection while computing route
  // on model editor or when clicking on fixed poi
  if (
    routeComputationStatus === RouteComputationStatus.CALCULATING ||
    findMyCar.status === FindMyCarStatusTypes.SETTING_POSITION ||
    poiId === fixedPoiID
  ) {
    return;
  }

  if (poiId) {
    // If the clicked POI does not exist, early return
    if (!currentBuildingPois.getPoiById(poiId) && !CUSTOM_POI_IDS.has(poiId)) {
      return;
    }

    // When filtering on secondary filter set navigation
    // to and from on different order
    if (!directionsSearchModeEnabled) {
      if (!navigationFrom && !navigationTo) {
        // Select feature ID if available
        featureId && store.dispatch(setCurrentFeatureID(featureId));

        if (uiMode === AppUI.directions) {
          store.dispatch(setNavigationTo(poiId));
        }
      } else if (
        !navigationFrom ||
        // allow setting other POIs as origin when current location was set but there is no current location now
        (currentLocationStatus !== LocationStatusTypes.POSITIONING &&
          navigationFrom === CURRENT_USER_LOCATION_ID)
      ) {
        store.dispatch(setNavigationFrom(poiId));
      } else if (!navigationTo || allowReselectingTargetOnRoute) {
        store.dispatch(setNavigationTo(poiId));
      } else {
        return;
      }
    } else {
      if (directionsSelectorNameSelected === FormSelectorMode.from) {
        store.dispatch(setNavigationFrom(poiId));
      } else if (directionsSelectorNameSelected === FormSelectorMode.to) {
        store.dispatch(setNavigationTo(poiId));
      }
      store.dispatch(setDirectionsSearchModeEnabled(false));
    }
  }

  // Handle poi ID selection
  handleSetCurrentPoiID({ poiId: poiId });
};

const isValidExternalFeature = (
  feature: ExternalFeature,
): feature is ExternalFeature => {
  return (
    feature &&
    feature.id &&
    feature.type === "Feature" &&
    feature.geometry &&
    feature.geometry.type === "Point" &&
    Array.isArray(feature.geometry.coordinates) &&
    feature.properties &&
    typeof feature.properties.floor_id === "number" &&
    typeof feature.properties.building_id === "number" &&
    feature.properties.building_id ===
      store.getState().cartography.currentBuildingID
  );
};

const normalizeExternalFeature = (
  feature: ExternalFeature,
): ExternalFeature => {
  if (feature.properties.tooltip != null || feature.properties.title == null) {
    return feature;
  }

  return {
    ...feature,
    properties: {
      ...feature.properties,
      tooltip: feature.properties.title,
    },
  };
};

export const handleExternalFeature = (
  rawExternalFeaturesPayload: ExternalFeature[],
) => {
  if (!Array.isArray(rawExternalFeaturesPayload)) return;
  const seenIds = new Set<string | number>(); // verify repeated IDs
  const values: ExternalFeature[] = rawExternalFeaturesPayload
    .filter((feature) => {
      if (!isValidExternalFeature(feature)) return false;
      if (seenIds.has(feature.id)) return false;
      seenIds.add(feature.id);
      return true;
    })
    .map(normalizeExternalFeature);
  store.dispatch(setExternalFeatures(values));
};
